import React, { useState } from 'react';
import './SettingInput.css';

const SettingInput = ({ 
  setting, 
  value, 
  onChange, 
  sectionKey, 
  settingKey 
}) => {
  // useState must be at the top level, not conditional
  const [inputValue, setInputValue] = useState('');
  
  const handleChange = (newValue) => {
    onChange(sectionKey, settingKey, newValue);
  };

  // Determine setting configuration based on the new API structure
  const getSettingConfig = (setting, settingKey) => {
    console.log(setting, settingKey);
    // Handle new API structure where setting is an object with default, min, max, options
    if (typeof setting === 'object' && setting.hasOwnProperty('default')) {
      const key = settingKey.toLowerCase();
      
      // Boolean settings (when default is boolean)
      if (typeof setting.default === 'boolean') {
        return {
          type: 'boolean',
          label: settingKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          default: setting.default,
          description: key === 'reverse' ? 'Reverse the order of results' : undefined
        };
      }
      
      // String settings (when default is string OR when default is array but options is null)
      if (typeof setting.default === 'string' || (Array.isArray(setting.default) && setting.options === null)) {
        return {
          type: 'str',
          label: settingKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          default: setting.default,
          options: setting.options, // if not null, will be a single-select dropdown
          placeholder: setting.options === null ? `Enter ${settingKey}` : undefined
        };
      }
      
      // Integer settings (when default is number and has min/max)
      if (typeof setting.default === 'number' && (setting.min !== undefined || setting.max !== undefined)) {
        return {
          type: 'int',
          label: settingKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          default: setting.default,
          min: setting.min,
          max: setting.max
        };
      }
      
      // List settings (when default is array AND has options) - CHECK THIS AFTER STRING
      if (Array.isArray(setting.default) && setting.options !== null) {
        return {
          type: 'list',
          label: settingKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          default: setting.default,
          options: setting.options, // if null, will allow free text input
          placeholder: setting.options === null ? `Enter ${settingKey} separated by commas` : undefined
        };
      }
    }
    
    // Fallback for old structure or direct boolean values
    if (typeof setting === 'boolean') {
      return {
        type: 'boolean',
        label: settingKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        default: setting
      };
    }
    
    // Fallback for any other case
    return {
      type: 'str',
      label: settingKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      default: setting
    };
  };
  
  const settingConfig = getSettingConfig(setting, settingKey);

  // Render different input types based on setting type
  switch (settingConfig.type) {
    case 'int':
      return (
        <div className="setting-input">
          <label className="setting-label">
            {settingConfig.label || settingKey}
            {settingConfig.description && <span className="setting-description">({settingConfig.description})</span>}
          </label>
          <input
            type="number"
            value={value !== undefined ? value : (settingConfig.default || '')}
            onChange={(e) => {
              const newValue = parseInt(e.target.value);
              if (!isNaN(newValue)) {
                // Validate against min/max constraints
                const minValue = settingConfig.min !== undefined ? settingConfig.min : Number.MIN_SAFE_INTEGER;
                const maxValue = settingConfig.max !== undefined ? settingConfig.max : Number.MAX_SAFE_INTEGER;
                const clampedValue = Math.max(minValue, Math.min(maxValue, newValue));
                handleChange(clampedValue);
              }
            }}
            min={settingConfig.min !== undefined ? settingConfig.min : undefined}
            max={settingConfig.max !== undefined ? settingConfig.max : undefined}
            className="setting-number-input"
          />
        </div>
      );

    case 'str':
      // If string has options, render as dropdown
      if (settingConfig.options && settingConfig.options.length > 0) {
        // Ensure default value is included in options
        const allOptions = [...settingConfig.options];
        if (settingConfig.default && !allOptions.includes(settingConfig.default)) {
          allOptions.unshift(settingConfig.default);
        }
        
        return (
          <div className="setting-input">
            <label className="setting-label">
              {settingConfig.label || settingKey}
              {settingConfig.description && <span className="setting-description">({settingConfig.description})</span>}
            </label>
            <select
              value={value !== undefined ? value : (settingConfig.default || '')}
              onChange={(e) => handleChange(e.target.value)} // Keep as string, not array
              className="setting-select-input"
            >
              {allOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        );
      }
      
      // If string has null options AND default is an array (multiple values allowed), render as tag input
      if (settingConfig.options === null && Array.isArray(settingConfig.default)) {

        
        const currentValues = (() => {
          const currentValue = value !== undefined ? value : settingConfig.default;
          if (typeof currentValue === 'string' && currentValue) {
            return currentValue.split(',').map(v => v.trim()).filter(v => v);
          }
          if (Array.isArray(currentValue)) {
            return currentValue.map(v => String(v).trim()).filter(v => v);
          }
          return [];
        })();
        
        
        const addValue = (newValue) => {
          if (newValue && !currentValues.includes(newValue)) {
            const updatedValues = [...currentValues, newValue];
            handleChange(updatedValues.join(', '));
          }
          setInputValue('');
        };
        
        const removeValue = (valueToRemove) => {
          const updatedValues = currentValues.filter(v => v !== valueToRemove);
          handleChange(updatedValues.join(', '));
        };
        
        return (
          <div className="setting-input">
            <label className="setting-label">
              {settingConfig.label || settingKey}
              {settingConfig.description && <span className="setting-description">({settingConfig.description})</span>}
            </label>
            {currentValues.length > 0 && (
              <div className="setting-tags">
                {currentValues.map((tag, index) => (
                  <span key={index} className="setting-tag">
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeValue(tag)}
                      className="setting-tag-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              type="text"
              value={inputValue}
              onChange={(e) => {
                const newValue = settingKey.toLowerCase().includes('symbol') ? e.target.value.toUpperCase() : e.target.value;
                setInputValue(newValue);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addValue(inputValue.trim());
                }
              }}
              onBlur={() => {
                if (inputValue.trim()) {
                  addValue(inputValue.trim());
                }
              }}
              placeholder={settingConfig.placeholder || 'Type and press Enter to add'}
              className="setting-text-input"
            />
          </div>
        );
      }
      
      // Otherwise render as simple text input (single value)
      return (
        <div className="setting-input">
          <label className="setting-label">
            {settingConfig.label || settingKey}
            {settingConfig.description && <span className="setting-description">({settingConfig.description})</span>}
          </label>
          <input
            type="text"
            value={value !== undefined ? value : (settingConfig.default || '')}
            onChange={(e) => {
              const newValue = settingKey.toLowerCase().includes('symbol') ? e.target.value.toUpperCase() : e.target.value;
              handleChange(newValue);
            }}
            placeholder={settingConfig.placeholder || ''}
            className="setting-text-input"
          />
        </div>
      );

    case 'list':
      // Two cases for lists:
      // 1. options: [...] -> Multi-select checkboxes 
      // 2. options: null -> Comma-separated text input
      if (settingConfig.options !== null && Array.isArray(settingConfig.options) && settingConfig.options.length > 0) {
        // Multi-select checkboxes for predefined options
        return (
          <div className="setting-input">
            <label className="setting-label">
              {settingConfig.label || settingKey}
              {settingConfig.description && <span className="setting-description">({settingConfig.description})</span>}
            </label>
            <div className="setting-multi-select">
              {settingConfig.options.map(option => (
                <label key={option} className="setting-checkbox-label">
                  <input
                    type="checkbox"
                    checked={(() => {
                      const currentValue = value !== undefined ? value : settingConfig.default;
                      const currentList = Array.isArray(currentValue) ? currentValue : [];
                      return currentList.includes(option);
                    })()}
                    onChange={(e) => {
                      const currentValue = value !== undefined ? value : settingConfig.default;
                      const currentList = Array.isArray(currentValue) ? currentValue : [];
                      if (e.target.checked) {
                        handleChange([...currentList, option]);
                      } else {
                        handleChange(currentList.filter(item => item !== option));
                      }
                    }}
                    className="setting-checkbox"
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        );
      } else {
        // Comma-separated text input when options is null (free text entry)
        return (
          <div className="setting-input">
            <label className="setting-label">
              {settingConfig.label || settingKey}
              {settingConfig.description && <span className="setting-description">({settingConfig.description})</span>}
            </label>
            <input
              type="text"
              value={(() => {
                const currentValue = value !== undefined ? value : settingConfig.default;
                if (Array.isArray(currentValue)) {
                  return currentValue.join(', ');
                }
                return currentValue || '';
              })()}
              onChange={(e) => {
                const items = e.target.value
                  .split(',')
                  .map(item => item.trim().toUpperCase())
                  .filter(item => item);
                handleChange(items);
              }}
              placeholder={settingConfig.placeholder || 'Enter values separated by commas'}
              className="setting-text-input"
            />
          </div>
        );
      }

    case 'boolean':
      return (
        <div className="setting-input">
          <label className="setting-checkbox-label">
            <input
              type="checkbox"
              checked={value !== undefined ? value : (settingConfig.default || false)}
              onChange={(e) => handleChange(e.target.checked)}
              className="setting-checkbox"
            />
            {settingConfig.label || settingKey}
            {settingConfig.description && <span className="setting-description">({settingConfig.description})</span>}
          </label>
        </div>
      );

    default:
      return (
        <div className="setting-input">
          <label className="setting-label">{settingConfig.label || settingKey}</label>
          <input
            type="text"
            value={value !== undefined ? value : (settingConfig.default || '')}
            onChange={(e) => handleChange(e.target.value)}
            className="setting-text-input"
          />
        </div>
      );
  }
};

export default SettingInput;